import { LaunchPageAppId } from "owa-m365-acquisitions/lib/data/M365AppId";
import { getPromiseForLocalizedString } from "owa-localize/lib/loc";
import { addApps, moreAppsAccessibleLabel, } from "owa-locstrings/lib/strings/moreapps.locstring.json";
import { isUnifiedAppStoreEnabled } from "owa-m365-acquisitions/lib/utils/isUnifiedAppStoreEnabled";
import { getAddInAppAcquisitionLink } from "./getAddinAppAcquisitionLink";
import { lazyIsAddInAppStoreEnabled } from "owa-m365-acquisitions/lib/lazyFunction";
import type { StrictAppAcquisitionLink } from "../types";
import type { MailboxInfo } from "owa-client-types";
import { isMOS3AppServiceAppsInMailAvailable } from "owa-m365-acquisitions/lib/utils/isMOS3AppServiceAppsInMailAvailable";
import { getApplicationSettings } from "owa-application-settings";
import { isMailboxSharedOrDelegate } from "owa-account-shared-mailbox-utils";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";

export async function getAppAcquisitionLinkData(isShownInAppBar: boolean): Promise<StrictAppAcquisitionLink> {
    return isShownInAppBar
        ? {
            id: LaunchPageAppId.AppStore,
            __typename: "AppAcquisitionLink",
            accessibleName: await getPromiseForLocalizedString(moreAppsAccessibleLabel),
            text: await getPromiseForLocalizedString(addApps),
            icon: {
                __typename: "InternalIcon",
                name: "AddSquareIcon",
            },
        }
        : {
            id: LaunchPageAppId.AppStoreModal,
            __typename: "AppAcquisitionLink",
            accessibleName: await getPromiseForLocalizedString(moreAppsAccessibleLabel),
            text: await getPromiseForLocalizedString(addApps),
            icon: {
                __typename: "InternalIcon",
                name: "AddSquareIcon",
            },
        };
}

export async function getAppAcquisitionLink(isShownInAppBar: boolean = true, mailboxInfo?: MailboxInfo): Promise<StrictAppAcquisitionLink | undefined> {
    // For shared or delegate mailboxes, we do not show the Add app/Get Addins in the flyout.
    if (isMailboxSharedOrDelegate(mailboxInfo ?? getModuleContextMailboxInfo())) {
        return;
    }
    
    if (isUnifiedAppStoreEnabled(mailboxInfo)) {
        return getAppAcquisitionLinkData(isShownInAppBar);
    }
    
    // If MOS3 returns add-ins for the environment, but the Unified Store URL is not set,
    // we need to hide the app acquisition link. Without this check we display the old add-ins store entry point
    if (isMOS3AppServiceAppsInMailAvailable(mailboxInfo) &&
        getApplicationSettings("Mos3Urls", mailboxInfo).UnifiedStoreUrl === "") {
        return;
    }
    // Lazy import util. It's ok if it doesn't import in time. Query will be re written post-boot when
    // apps are fetched.
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const isAddInAppStoreEnabled = lazyIsAddInAppStoreEnabled.tryImportForRender();
    if (!isShownInAppBar && isAddInAppStoreEnabled?.(mailboxInfo)) {
        return getAddInAppAcquisitionLink();
    }
    return;
}
